import { listsV2 } from '@ecosystem/newron-design-system';

import { ColumnConfiguration } from '../context-selector-v2';
import { SortSettings } from './types';

export const getUniqueValues = <T>(array: T[]): T[] => {
  return Array.from(new Set(array));
};

/**
 * A utility function to check if a value is date-like.
 * It checks if the value is a string and can be parsed into a valid date.
 * @param val - The value to check.
 * @returns True if the value is date-like, false otherwise.
 */
export const isDateLike = (val: unknown): boolean => typeof val === 'string' && !isNaN(Date.parse(val));

/**
 * A universal comparison function that can handle various types of values.
 * It supports numbers, dates, and strings, and can handle undefined values.
 * It throws an error for unsupported types.
 * @param a the first value to compare
 * @param b the second value to compare
 * @returns a negative number if a < b, a positive number if a > b, and 0 if they are equal
 */
export const universalCompare = (a: unknown, b: unknown): number => {
  if (a === undefined && b === undefined) return 0;
  if (a === undefined) return 1;
  if (b === undefined) return -1;

  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }

  if (a instanceof Date && b instanceof Date) {
    return a.getTime() - b.getTime();
  }

  if (isDateLike(a) && isDateLike(b)) {
    return new Date(a as string).getTime() - new Date(b as string).getTime();
  }
  if (typeof a !== 'string' || typeof b !== 'string') {
    throw new Error(`Unsupported type for sorting: ${typeof a} or ${typeof b}`);
  }
  return a.localeCompare(b, undefined, { sensitivity: 'base' });
};

/**
 * Sorts a primitive array or an array of objects when provided a specific key.
 * @param array The array to sort.
 * @param sortSettings The sorting settings to apply.
 * @param getSortingValue A function to retrieve the sorting key from each object.
 * @returns The sorted array.
 */
export const sortByKey = <T>(
  array: T[],
  sortSettings: SortSettings<T>,
  getSortingValue?: (object: T, key: keyof T) => string | number | Date
): T[] => {
  const { id: key, sortingOrder } = sortSettings;
  if (array.length === 0) return array;

  return [...array].sort((a, b) => {
    const aVal: unknown = getSortingValue ? getSortingValue(a, key) : a[key];
    const bVal: unknown = getSortingValue ? getSortingValue(b, key) : b[key];
    const result = universalCompare(aVal, bVal);
    return sortingOrder === 'asc' ? result : -result;
  });
};

/**
 * Returns a common column configuration for the table.
 * This function is used to create a consistent column configuration across different tables.
 * @param columnConfig The column configuration to be used.
 * @returns A common column configuration object.
 */
export const getTableCommonColumnConfig = (columnConfig = {} as ColumnConfiguration): listsV2.ColumnConfig => {
  return {
    ...columnConfig,
    cellConfig: {
      hasRightBorder: false,
      hasBottomBorder: false,
    },
  };
};
